import { useLayoutEffect } from "react";
import { Check, X } from "lucide-react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { FormBuilderPanel } from "@/features/form-editor/components/form-builder/form-builder-panel";
import { FormEditorStoreHydrator } from "@/features/form-editor/components/form-editor-store-hydrator";
import { FormEditorUnsavedChangesModal } from "@/features/form-editor/components/form-editor-unsaved-changes-modal";
import { FormPreviewEmptyState } from "@/features/form-editor/components/form-preview-empty-state";
import { FormRulesTabPanel } from "@/features/form-editor/components/form-rules-tab-panel";
import { FormEditorLayoutProvider } from "@/features/form-editor/context/form-editor-layout-context";
import type { FormEditorEntity } from "@/features/form-editor/form-editor-entity.types";
import { useCanCreateFormLayout } from "@/features/form-editor/hooks/use-can-create-form-layout";
import { useFormDefinitionEditorPersistence } from "@/features/form-editor/hooks/use-form-definition-editor-persistence";
import type { FormEditorScope } from "@/features/form-editor/lib/build-form-editor-paths";
import {
	type EntityTypeFormMode,
	useEntityTypeFormMode,
	useEntityTypeFormModeStore,
} from "@/features/form-editor/state/entity-type-form-mode-store";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { DynamicForm } from "@/lib/dynamicForms/components/DynamicForm.component";
import { DynamicFormHostProvider } from "@/lib/dynamicForms/contexts/dynamic-form-host-context";

const EMPTY_KEY: Record<FormEditorEntity, string> = {
	job: "admin.jobTypes.drawer.form.empty",
	ticket: "admin.ticketTypes.drawer.form.empty",
	asset: "admin.assetTypes.drawer.form.empty",
	record: "admin.recordTypes.drawer.form.empty",
};

export interface EntityTypeFormTabProps {
	entity: FormEditorEntity;
	typeId: string;
	scope: FormEditorScope;
	orgId?: string;
	mutationOrganizationId?: string;
	/** When set, replaces the CreateFormLayout check. */
	canEdit?: boolean;
}

/**
 * Preview, Build, and Rules for one entity type. The mode and unsaved draft
 * live outside this component so the drawer and the page share them.
 */
export function EntityTypeFormTab({
	entity,
	typeId,
	scope,
	orgId,
	mutationOrganizationId,
	canEdit: canEditProp,
}: EntityTypeFormTabProps) {
	const { t } = useAppTranslation();
	const canCreateFormLayout = useCanCreateFormLayout();
	const canEdit = canEditProp ?? canCreateFormLayout;
	const storedMode = useEntityTypeFormMode(typeId);
	const setMode = useEntityTypeFormModeStore((state) => state.setMode);
	const mode: EntityTypeFormMode = canEdit ? storedMode : "preview";

	const editor = useFormDefinitionEditorPersistence({
		entity,
		typeId,
		scope,
		orgId,
		variant: "full",
		canEdit,
		mutationOrganizationId,
		saveStrategy: "versionedActivate",
		retainSiblingFormSurfaces: true,
	});

	useLayoutEffect(() => {
		const state = useEntityTypeFormModeStore.getState();
		for (const id of Object.keys(state.modeByTypeId)) {
			if (id !== typeId) state.clearMode(id);
		}
	}, [typeId]);

	const items: { id: EntityTypeFormMode; label: string }[] = [
		{
			id: "preview",
			label: t("admin.entityTypes.formMode.preview"),
		},
	];
	if (canEdit) {
		items.push(
			{ id: "build", label: t("admin.entityTypes.formMode.build") },
			{ id: "rules", label: t("admin.entityTypes.formMode.rules") },
		);
	}

	const organizationId = mutationOrganizationId ?? orgId ?? "";

	function renderBody() {
		if (editor.remoteFormQuery.isPending) {
			return (
				<div
					className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 py-16"
					aria-busy
				>
					<LoadingIndicator type="line-simple" size="md" supportingText />
				</div>
			);
		}

		if (editor.remoteFormQuery.isError) {
			return (
				<div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 py-16">
					<p className="text-center text-sm text-text-secondary">
						{t("common.actions.errorLoading")}
					</p>
					<Button
						color="primary"
						size="sm"
						type="button"
						onClick={() => {
							void editor.remoteFormQuery.refetch();
						}}
					>
						{t("common.actions.retry")}
					</Button>
				</div>
			);
		}

		if (editor.editorHydrationKey == null) return null;

		const preview =
			mode === "preview" ? (
				editor.previewSchema.fieldDefinitions.length === 0 ? (
					<FormPreviewEmptyState emptyKey={EMPTY_KEY[entity]} />
				) : (
					<DynamicFormHostProvider
						value={{ organizationId, runtime: false }}
					>
						<DynamicForm schema={editor.previewSchema} variant="preview" />
					</DynamicFormHostProvider>
				)
			) : null;

		return (
			<FormEditorStoreHydrator
				key={editor.editorHydrationKey}
				hydrationKey={editor.editorHydrationKey}
				schema={editor.editorSchema}
				formRules={editor.editorFormRules}
			>
				<FormEditorLayoutProvider isReadOnly={!canEdit}>
					{mode === "preview" ? preview : null}
					{mode === "build" ? <FormBuilderPanel /> : null}
					{mode === "rules" ? <FormRulesTabPanel /> : null}
				</FormEditorLayoutProvider>
			</FormEditorStoreHydrator>
		);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-4 px-5 pb-5">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<Tabs
					selectedKey={mode}
					onSelectionChange={(key) => {
						const next = String(key) as EntityTypeFormMode;
						if (next !== "preview" && !canEdit) return;
						setMode(typeId, next);
					}}
					className="w-auto"
				>
					<Tabs.List
						type="button-border"
						size="sm"
						items={items}
						data-testid="entity-type-form-mode"
					>
						{(tab) => <Tabs.Item id={tab.id} label={tab.label} />}
					</Tabs.List>
				</Tabs>
				{mode === "preview" ? null : (
					<div className="flex flex-wrap items-center gap-2">
						<Button
							color="secondary"
							size="sm"
							type="button"
							iconLeading={X}
							onClick={() => {
								editor.revertUnsavedEdits();
							}}
						>
							{t(editor.paths.formEditor.cancel)}
						</Button>
						<Button
							size="sm"
							type="button"
							isDisabled={!canEdit || !editor.isDirty}
							isLoading={editor.isSaving}
							data-testid="form-editor-save-btn"
							className="bg-utility-orange-500 text-white hover:bg-utility-orange-600"
							iconLeading={<Check className="size-4 text-white" aria-hidden />}
							onClick={() => {
								void editor.handleSave({ navigateAfterSave: false });
							}}
						>
							{t(editor.paths.formEditor.save)}
						</Button>
					</div>
				)}
			</div>
			<div className="flex min-h-0 flex-1 flex-col">{renderBody()}</div>
			<FormEditorUnsavedChangesModal
				isOpen={editor.unsavedPrompt.isOpen}
				isApplying={editor.unsavedPrompt.isApplying}
				isSaveInFlight={editor.unsavedPrompt.isSaveInFlight}
				paths={editor.paths}
				onStay={editor.unsavedPrompt.onStay}
				onDiscard={editor.unsavedPrompt.onDiscard}
				onApply={editor.unsavedPrompt.onApply}
			/>
		</div>
	);
}
